class ShopFilters extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-group]'));
    this.grid = document.getElementById(this.getAttribute('grid-id'));
    this.items = this.grid ? Array.from(this.grid.querySelectorAll('[data-fit]')) : [];

    this.items.forEach((item) => {
      try {
        item._catalogColorSlides = JSON.parse(item.dataset.colorSlides || '{}');
      } catch (error) {
        item._catalogColorSlides = {};
      }

      try {
        item._catalogColorUrls = JSON.parse(item.dataset.colorUrls || '{}');
      } catch (error) {
        item._catalogColorUrls = {};
      }

      item._catalogLinks = Array.from(item.querySelectorAll('a[href*="/products/"]'));
      item._catalogLinks.forEach((link) => {
        link._catalogOriginalHref = link.getAttribute('href');
      });

      // Reverse-index slide id -> color slug so a swiped-to slide can update
      // the card's link, and the color filter can look up which slides
      // belong to which color.
      item._catalogSlideToColor = {};
      Object.entries(item._catalogColorSlides).forEach(([colorSlug, ids]) => {
        ids.forEach((id) => {
          item._catalogSlideToColor[String(id)] = colorSlug;
        });
      });

      const slideshow = item.querySelector('slideshow-component');
      const slides = slideshow ? Array.from(slideshow.querySelectorAll('slideshow-slide')) : [];
      item._catalogSlideshow = slideshow;
      item._catalogSlides = slides;
      item._catalogDefaultSlideId = slides[0]?.getAttribute('slide-id') || null;
    });

    // Keep each card's link pointed at whichever color is currently shown,
    // whether that's from a filter selection or the customer swiping/
    // dragging the photo carousel themselves.
    this.grid?.addEventListener('slideshow:select', (event) => {
      const slide = event.detail?.slide;
      const item = slide?.closest('.shop-catalogue__item');
      if (!item || !item._catalogLinks?.length) return;

      const colorSlug = item._catalogSlideToColor[String(event.detail.id)];
      const colorUrl = colorSlug ? item._catalogColorUrls[colorSlug] : null;

      item._catalogLinks.forEach((link) => {
        link.setAttribute('href', colorUrl || link._catalogOriginalHref);
      });
    });

    // Swiping should be the only way to browse a card's photos. The theme's
    // hover-to-preview-second-image behavior is wired up by the framework
    // looking for these on:pointerenter/on:pointerleave attributes at event
    // time (global delegated listeners), so removing the attributes - not
    // just intercepting the events - is what actually disables it, scoped
    // to this grid without touching the global theme setting.
    this.grid?.querySelectorAll('[on\\:pointerenter], [on\\:pointerleave]').forEach((el) => {
      el.removeAttribute('on:pointerenter');
      el.removeAttribute('on:pointerleave');
    });

    this.groups = Array.from(new Set(this.buttons.map((button) => button.dataset.group)));
    this.active = {};

    const params = new URLSearchParams(window.location.search);

    this.groups.forEach((group) => {
      const groupButtons = this.buttons.filter((button) => button.dataset.group === group);
      groupButtons.forEach((button) => {
        button.addEventListener('click', () => this.activate(group, button.dataset.value, true));
      });

      const requested = params.get(group) || 'all';
      const valid = groupButtons.some((button) => button.dataset.value === requested) ? requested : 'all';
      this.active[group] = valid;
    });

    this.groups.forEach((group) => this.render(group));
    this.filterItems();
  }

  activate(group, value, updateUrl) {
    this.active[group] = value;
    this.render(group);
    this.filterItems();

    if (updateUrl) {
      const url = new URL(window.location.href);
      if (value === 'all') {
        url.searchParams.delete(group);
      } else {
        url.searchParams.set(group, value);
      }
      window.history.replaceState({}, '', url);
    }
  }

  render(group) {
    this.buttons
      .filter((button) => button.dataset.group === group)
      .forEach((button) => {
        const isActive = button.dataset.value === this.active[group];
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', String(isActive));
      });
  }

  filterItems() {
    this.updateAvailableColors();

    this.items.forEach((item) => {
      const matches = this.groups.every((group) => {
        const value = this.active[group];
        if (value === 'all') return true;
        const itemValues = (item.dataset[group] || '').split(' ');
        return itemValues.includes(value);
      });
      item.hidden = !matches;
    });

    this.updateColorSlides();
  }

  // Narrows the Base Color swatches to only colors that exist on products
  // matching the current Fit + Graphic selection, so picking e.g. "Oversized"
  // hides colors that no oversized product actually comes in. If the
  // currently selected color is no longer available, resets it to "All".
  updateAvailableColors() {
    if (!this.groups.includes('base')) return;

    const activeFit = this.active.fit;
    const activeGraphic = this.active.graphic;
    const availableColors = new Set();

    this.items.forEach((item) => {
      const fits = (item.dataset.fit || '').split(' ');
      const graphics = (item.dataset.graphic || '').split(' ');
      const matchesFit = !activeFit || activeFit === 'all' || fits.includes(activeFit);
      const matchesGraphic = !activeGraphic || activeGraphic === 'all' || graphics.includes(activeGraphic);
      if (!matchesFit || !matchesGraphic) return;

      (item.dataset.base || '').split(' ').forEach((base) => {
        if (base && base !== 'all') availableColors.add(base);
      });
    });

    this.buttons
      .filter((button) => button.dataset.group === 'base' && button.dataset.value !== 'all')
      .forEach((button) => {
        button.hidden = !availableColors.has(button.dataset.value);
      });

    if (this.active.base !== 'all' && !availableColors.has(this.active.base)) {
      this.active.base = 'all';
      this.render('base');

      const url = new URL(window.location.href);
      url.searchParams.delete('base');
      window.history.replaceState({}, '', url);
    }
  }

  updateColorSlides() {
    const activeBase = this.active.base;

    this.items.forEach((item) => {
      const slides = item._catalogSlides;
      const slideshow = item._catalogSlideshow;
      if (!slides || !slides.length || !slideshow) return;

      // Cards only render their first 2 images, so ignore this color's photos
      // that aren't on the card; if none are, fall back to the card's 2 images
      // instead of hiding every slide.
      const renderedIds = new Set(slides.map((slide) => slide.getAttribute('slide-id')));
      const allowedIds =
        activeBase && activeBase !== 'all'
          ? (item._catalogColorSlides[activeBase] || []).filter((id) => renderedIds.has(String(id)))
          : null;

      let targetSlideId = item._catalogDefaultSlideId;

      if (allowedIds && allowedIds.length) {
        const allowedSet = new Set(allowedIds.map(String));
        slides.forEach((slide) => {
          const isAllowed = allowedSet.has(slide.getAttribute('slide-id'));
          slide.toggleAttribute('hidden', !isAllowed);
        });
        const firstAllowedSlide = slides.find((slide) => allowedSet.has(slide.getAttribute('slide-id')));
        if (firstAllowedSlide) targetSlideId = firstAllowedSlide.getAttribute('slide-id');
      } else {
        // No color filter: show every color's front+back pair, in order,
        // instead of Shopify's native variant-image hiding (which keeps
        // only one photo for every color after the first).
        slides.forEach((slide) => {
          slide.removeAttribute('hidden');
        });
      }

      if (targetSlideId && typeof slideshow.select === 'function') {
        // Force the index stale so select() doesn't treat the target as
        // already-current just because the newly filtered slide list
        // happens to put it at the same index the old one was at.
        slideshow.current = -1;
        slideshow.select({ id: targetSlideId }, undefined, { animate: false });
      }
    });
  }
}

if (!customElements.get('shop-filters')) {
  customElements.define('shop-filters', ShopFilters);
}
